// Recovering the AI's original draft of a report paragraph from its edit history.
//
// WHY THIS IS NEEDED AT THE POINT OF THE EDIT
//
// The adoption numbers compare what the AI drafted with what the clinician
// settled on. Firestore stores the CURRENT text and a history of changes, not the
// original draft, so the draft has to be reconstructed. Doing that in a nightly
// job means reading the report text back out of Firestore; doing it here means
// using the edit history the review page has ALREADY read inside its own write
// transaction, so no extra read exists for analytics to be blamed for.
//
// THE RULE, WHICH MUST NOT DRIFT FROM THE PYTHON ONE
//
// The draft is the `previousValue` of the EARLIEST MANUAL OR RESET entry, never
// of a regeneration. A regeneration replaces one AI draft with another, so its
// previousValue is a previous draft rather than anything a clinician wrote;
// reading it as the original would compare two AI drafts and report a paragraph
// as heavily edited by nobody. When the history holds no clinician change at all,
// there is no recovered draft and the caller's current text IS the draft.
//
// This mirrors `_edit_history_summary` in
// python-functions/functions/analytics/dimension_export.py, which produced the
// historical figures. Agreement is pinned against a Python-generated fixture
// (__tests__/reportDraftRecovery.test.js), because two implementations of one
// rule in two languages drift the moment nothing is checking.
//
// KNOWN LIMITATION, in both directions, and neither is recoverable from what is
// stored - which is why the regeneration count travels alongside:
//   - Regenerated BEFORE any manual edit: the recovered draft is the second
//     draft, so the numbers describe adoption of that draft, not the first.
//   - Regenerated AFTER a manual edit: the final text is a fresh AI draft nobody
//     has touched, but it is compared against the draft that was edited, so it
//     reads as edited when the current text is pure AI.

/**
 * What one edit-history entry records.
 *
 * Three writers, three vocabularies. The review pages write `editType: 'manual'`
 * and mark the other two with booleans (`isReset`, `isDeleted`) rather than a
 * type; the backend writes `editType: 'regeneration'`; RMS writes 'manual_edit'
 * and 'reset'. All are read, because an entry read as the wrong kind moves a
 * paragraph between the accepted and edited columns.
 *
 * An entry with no recognisable marker counts as a manual edit: it carries a
 * previousValue and a clinician, which is what a manual edit is, and reading it
 * as a regeneration instead would let it silently replace the AI draft.
 */
export function editEntryKind(entry) {
  if (!entry || typeof entry !== 'object') return 'manual';
  if (entry.isDeleted === true) return 'deleted';
  const editType = entry.editType == null ? '' : String(entry.editType);
  if (entry.isReset === true || editType === 'reset') return 'reset';
  if (editType === 'regeneration') return 'regeneration';
  return 'manual';
}

/**
 * Order entries by when they were written.
 *
 * Both writers key an entry by milliseconds since the epoch as a STRING
 * (`Date.now().toString()` in the review pages, `str(int(time.time() * 1000))`
 * in the backend), so lexicographic order is only correct while every key has the
 * same number of digits. Sorting numerically is what keeps "earliest edit"
 * honest. A key that is not a number sorts after the ones that are,
 * deterministically, rather than throwing.
 */
function sortKey(key) {
  const text = String(key);
  const isDigits = text.length > 0 && /^[0-9]+$/.test(text);
  return isDigits ? [0, Number(text), ''] : [1, 0, text];
}

function compareKeys(a, b) {
  const ka = sortKey(a);
  const kb = sortKey(b);
  if (ka[0] !== kb[0]) return ka[0] - kb[0];
  if (ka[1] !== kb[1]) return ka[1] - kb[1];
  if (ka[2] < kb[2]) return -1;
  if (ka[2] > kb[2]) return 1;
  return 0;
}

/**
 * Reconstruct the AI draft and count what happened to one paragraph.
 *
 * @param {object} history - The paragraph's editHistory map, keyed by write time.
 * @returns {{draftText: (string|null), manualEditCount: number,
 *   regenerationCount: number, isDeleted: boolean}} draftText is null when the
 *   history holds no clinician change, in which case the current text is the draft.
 */
export function recoverDraft(history) {
  const summary = {
    draftText: null,
    manualEditCount: 0,
    regenerationCount: 0,
    isDeleted: false,
  };
  if (!history || typeof history !== 'object' || Array.isArray(history)) return summary;

  const keys = Object.keys(history)
    .filter((key) => history[key] && typeof history[key] === 'object'
      && !Array.isArray(history[key]))
    .sort(compareKeys);

  keys.forEach((key) => {
    const entry = history[key];
    const kind = editEntryKind(entry);
    if (kind === 'deleted') {
      summary.isDeleted = true;
      return;
    }
    if (kind === 'regeneration') {
      summary.regenerationCount += 1;
      return;
    }
    if (summary.draftText === null) {
      // Python: str(entry.get('previousValue') or ''), so a missing, null or
      // empty previousValue recovers as an empty draft rather than as "no draft
      // found" - the entry did happen, and treating it as absent would fall back
      // to the current text and report the paragraph as accepted unchanged.
      const previous = entry.previousValue;
      summary.draftText = previous == null || previous === false || previous === 0
        ? '' : String(previous);
    }
    if (kind === 'manual') summary.manualEditCount += 1;
  });

  return summary;
}

/**
 * The text to treat as the AI's draft, given a paragraph's history and the text
 * as it stood immediately before the change being recorded.
 *
 * On a paragraph's FIRST edit the history holds no clinician change, so the text
 * before that edit is the draft. Afterwards the recovered draft wins, because the
 * text before a second edit is the clinician's own earlier wording.
 *
 * @param {object} priorHistory - The paragraph's editHistory BEFORE this change.
 * @param {string} textBeforeThisChange
 * @returns {{draftText: string, regenerationCount: number}}
 */
export function draftForComparison(priorHistory, textBeforeThisChange) {
  const recovered = recoverDraft(priorHistory);
  return {
    draftText: recovered.draftText === null
      ? String(textBeforeThisChange == null ? '' : textBeforeThisChange)
      : recovered.draftText,
    regenerationCount: recovered.regenerationCount,
  };
}
